import React, { useMemo, useState } from "react";
import Image from "next/image";
import { FaTrophy, FaStar, FaTimes } from "react-icons/fa";
import { awardsData } from "../../utils/constants/Awards";
import AwardCard from "./AwardCard";
import Container from "../common/Container";

// Awards & Recognition Page (static except snowfall)
const AwardsAndRecognition = () => {
  const [activeYear, setActiveYear] = useState("All");
  const [activeAward, setActiveAward] = useState(null);

  const sorted = useMemo(
    () => [...awardsData].sort((a, b) => Number(b.year) - Number(a.year)),
    []
  );

  const years = useMemo(
    () => ["All", ...new Set(sorted.map((a) => a.year))],
    [sorted]
  );

  const filtered =
    activeYear === "All" ? sorted : sorted.filter((a) => a.year === activeYear);

  const latestYear = sorted[0]?.year;

  return (
    <div className="page-root relative min-h-screen text-white overflow-hidden font-lexend">
      <Container className="relative mx-auto w-full px-4 pb-20 pt-10 sm:px-6 lg:px-8">
        {/* HERO (static gradient + falling snow) */}
        <header className="hero relative isolate overflow-hidden rounded-[2.2rem] p-6 shadow-2xl sm:p-10">
          {/* base gradient */}
          <div className="hero-base absolute inset-0 -z-20" />

          {/* snow overlay (falling) */}
          <div className="hero-snow pointer-events-none absolute inset-0 -z-10 snow snow--back" />
          <div className="hero-snow pointer-events-none absolute inset-0 -z-10 snow snow--front" />

          {/* subtle sheen + vignette for readability */}
          <div className="pointer-events-none absolute inset-0 -z-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.10),transparent_60%)]" />
          <div className="pointer-events-none absolute inset-0 -z-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(0,0,0,0.35))]" />

          <div className="relative z-10 flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90 ring-1 ring-white/20">
                <FaStar className="opacity-90" />
                Awards & Recognitions
              </div>

              <h1 className="mt-4 !text-[32px] font-semibold tracking-tight sm:!text-5xl">
                Celebrating milestones{" "}
                <span className="text-white/80">that matter</span>
              </h1>

              <p className="mt-3 max-w-2xl text-sm text-white/80 sm:text-base">
                Every recognition marks a step forward — powered by our
                learners, creators, and a team obsessed with impact.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="rounded-2xl border border-white/20 bg-black/30 px-4 py-3">
                  <div className="text-xs text-white/70">Latest Year</div>
                  <div className="text-xl font-semibold tracking-wide">
                    {latestYear}
                  </div>
                </div>
                <div className="rounded-2xl border border-white/20 bg-black/30 px-4 py-3">
                  <div className="text-xs text-white/70">Total Awards</div>
                  <div className="text-xl font-semibold tracking-wide">
                    {awardsData?.length}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative hidden h-44 w-44 shrink-0 sm:block">
              <div className="absolute inset-0 rounded-[2rem] bg-white/10 ring-1 ring-white/20 backdrop-blur-xl" />
              <div className="absolute inset-2 rounded-[1.6rem] bg-white/10 blur-xl" />
              <div className="absolute inset-0 grid place-items-center">
                <FaTrophy className="text-5xl text-white/95 drop-shadow" />
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white/10 to-transparent" />
        </header>

        {/* FILTER PILLS */}
        <div className="mt-8 flex flex-wrap gap-2">
          {years?.map((y) => {
            const isActive = activeYear === y;
            return (
              <button
                key={y}
                onClick={() => setActiveYear(y)}
                className={[
                  "rounded-full px-3 py-1 text-xs ring-1 transition-all duration-300",
                  isActive
                    ? "bg-white text-black ring-white/80 shadow-[0_0_20px_rgba(255,255,255,0.25)]"
                    : "bg-white/5 text-white/80 ring-white/10 hover:bg-white/10",
                ].join(" ")}
              >
                {y}
              </button>
            );
          })}
        </div>

        {/* GRID */}
        <section className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered?.map((award) => (
            <AwardCard key={award?.id} award={award} onOpen={setActiveAward} />
          ))}
        </section>

        {/* QUOTE BANNER */}
        <section className="mt-12">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0f18]/80 p-6 shadow-2xl sm:p-8">
            {/* ambient blobs */}
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-purple-500/25 blur-3xl" />
            <div className="pointer-events-none absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
            {/* subtle pattern */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.08] bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:18px_18px]" />

            <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              {/* left */}
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <FaTrophy className="text-xl text-white/90" />
                </div>

                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium text-white/80 ring-1 ring-white/20">
                    <FaStar className="text-[10px]" />
                    Our Journey
                  </div>

                  <h3 className="mt-3 text-xl font-semibold tracking-tight sm:text-2xl">
                    “Recognition is the result.
                    <span className="text-white/90">
                      {" "}
                      Learning is the reason.”
                    </span>
                  </h3>

                  <p className="mt-2 max-w-2xl text-sm text-white/70">
                    Every recognition reflects thousands of learners and
                    creators moving forward together.
                  </p>
                </div>
              </div>

              {/* right chips */}
            </div>

            {/* bottom glow line */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          </div>
        </section>
      </Container>

      {/* MODAL */}
      {activeAward && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setActiveAward(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-[2rem] border border-white/10 bg-[#070a10] shadow-2xl"
          >
            <button
              onClick={() => setActiveAward(null)}
              className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white ring-1 ring-white/10 hover:bg-black/80"
            >
              <FaTimes />
            </button>

            <div className="relative h-96 w-full">
              <Image
                src={activeAward?.image}
                alt={activeAward?.title}
                width={500}
                height={500}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-xs text-white/70">{activeAward.org}</div>
                <h3 className="mt-1 text-2xl font-semibold">
                  {activeAward?.title}
                </h3>
                <div className="mt-1 text-sm text-white/80">
                  {activeAward?.subtitle}
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm leading-relaxed text-white/75">
                {activeAward?.description}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10">
                  {activeAward?.year}
                </span>
                <span className="rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10">
                  {activeAward?.badge}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS (only snow animates) */}
      <style>{`
        /* Page simple background */
        .page-root {
          background:
            radial-gradient(1200px circle at 10% -10%, rgba(124,58,237,0.35), transparent 55%),
            radial-gradient(900px circle at 90% 0%, rgba(236,72,153,0.20), transparent 60%),
            linear-gradient(180deg, #05070b 0%, #070912 50%, #05070b 100%);
        }

        /* Hero base gradient */
        .hero-base {
          background: radial-gradient(120% 140% at 10% -10%,
            rgba(167,139,250,0.95),
            rgba(88,28,135,0.9) 45%,
            rgba(88,28,135,0.9) 100%);
        }

        /* Snow base (shared) */
        .snow {
          position: absolute;
          inset: -25%;
          background-size: 200px 200px;
          mix-blend-mode: screen;
          filter: drop-shadow(0 0 5px rgba(255,255,255,0.5));
          will-change: transform;
        }

        /* back layer = smaller flakes, slower */
        .snow--back {
          background-image:
            radial-gradient(1.6px 1.6px at 10% 20%, rgba(255,255,255,0.9), transparent 60%),
            radial-gradient(1.2px 1.2px at 25% 75%, rgba(255,255,255,0.85), transparent 60%),
            radial-gradient(1.8px 1.8px at 40% 35%, rgba(255,255,255,0.9), transparent 60%),
            radial-gradient(1.3px 1.3px at 55% 60%, rgba(255,255,255,0.85), transparent 60%),
            radial-gradient(1.9px 1.9px at 70% 15%, rgba(255,255,255,0.95), transparent 60%),
            radial-gradient(1.4px 1.4px at 85% 45%, rgba(255,255,255,0.9), transparent 60%);
          opacity: 0.65;
          animation: snowFallBack 22s linear infinite;
        }

        /* front layer = bigger flakes, faster */
        .snow--front {
          background-image:
            radial-gradient(2.6px 2.6px at 12% 18%, rgba(255,255,255,1), transparent 60%),
            radial-gradient(2.2px 2.2px at 28% 80%, rgba(255,255,255,0.98), transparent 60%),
            radial-gradient(2.8px 2.8px at 42% 35%, rgba(255,255,255,1), transparent 60%),
            radial-gradient(2.1px 2.1px at 58% 62%, rgba(255,255,255,0.98), transparent 60%),
            radial-gradient(3px 3px at 72% 12%, rgba(255,255,255,1), transparent 60%),
            radial-gradient(2.3px 2.3px at 86% 46%, rgba(255,255,255,0.98), transparent 60%),
            radial-gradient(2.7px 2.7px at 80% 74%, rgba(255,255,255,1), transparent 60%);
          opacity: 0.9;
          animation: snowFallFront 14s linear infinite;
        }

        @keyframes snowFallBack {
          from { transform: translateY(-10%); }
          to   { transform: translateY(10%); }
        }

        @keyframes snowFallFront {
          from { transform: translateY(-16%); }
          to   { transform: translateY(16%); }
        }

        /* Respect reduced-motion */
        @media (prefers-reduced-motion: reduce) {
          .snow--back, .snow--front { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AwardsAndRecognition;
